import React, { useEffect, useRef, useState } from 'react'
import OrderService from '../service/OrderService';
import '../css/checkout.css'
import '../css/admin.css'
import { useHistory, useLocation } from "react-router-dom";
import { HiOutlineCash, HiOutlinePhone, HiOutlineEmojiSad, HiOutlineInbox, HiCheckCircle } from 'react-icons/hi';
import { MdNavigateNext } from 'react-icons/md';
import { formatRupee } from '../components/Price';
import Skeleton from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import Modal from '../components/Modal';
import Toast from '../components/Toast';
import { statusFromCode, statusLabel, statusTone } from '../util/orderStatus';

// how the customer says they paid. UPI and card are confirmed by Razorpay and never listed here; a UPI request
// is an older direct-UPI payment with a UTR, and the oldest requests have no method at all
const methodLabel = (req) => req.method === 'WHATSAPP' ? 'WhatsApp' : (req.method === 'UPI' ? 'UPI (older payment)' : 'Manual payment')

const payReference = (req) => {
    if (req.method === 'WHATSAPP') return 'Paid on WhatsApp'
    return req.utr ? `UPI · UTR ${req.utr}` : methodLabel(req)
}

const FILTERS = [
    { value: 0, label: 'All' },
    { value: 1, label: 'Confirmed' },
    { value: 2, label: 'Processing' },
    { value: 3, label: 'Packaged' },
    { value: 4, label: 'Shipped' },
    { value: 5, label: 'Delivered' }
]

// Deep links from the dashboard: ?status=1 (a filter chip) and ?view=payments (scroll to payments to verify).
const statusFromSearch = (search) => {
    const q = new URLSearchParams(search)
    const value = Number(q.get('status'))
    return q.has('status') && FILTERS.some(f => f.value === value) ? value : 1
}

const AdminOrderView = () => {

    const history = useHistory();
    const location = useLocation();
    const paymentsRef = useRef(null)
    const wantsPayments = new URLSearchParams(location.search).get('view') === 'payments'

    const [orders, setOrders] = useState()
    const [status, setStatus] = useState(() => statusFromSearch(location.search))
    const [highlightPayments, setHighlightPayments] = useState(false)
    const [requests, setRequests] = useState()
    const [showPopup, setShowPopup] = useState(false)
    const [refreshKey, setRefreshKey] = useState(0)
    const [selectedObj, setSelectedObj] = useState({})
    const [isLoading, setIsLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState('')
    const [toast, setToast] = useState('')

    const redirctAdminEachOrder = (param) => {
        history.push({
            pathname: '/orders/admin/view/' + param,
            state: { id: param }
        })
    }

    useEffect(() => {
        setIsLoading(true)
        setLoadError(false)
        OrderService.getAdminOrdersList(status).then(res => {
            if (res.status) {
                setOrders(res.data.data.listOrders)
                setRequests(res.data.data.listPays)
            }
            setIsLoading(false)
        }).catch(error => {
            console.log(error)
            if (error?.response?.status === 401) {
                history.push('/login')
                return
            }
            setLoadError(true)
            setIsLoading(false)
        });
    }, [status, refreshKey, history])

    // the chosen filter goes in the URL so the view can be shared and survives reload
    const selectStatus = (value) => {
        setStatus(value)
        history.replace({ pathname: '/admin/orders', search: '?status=' + value })
    }

    useEffect(() => {
        if (!wantsPayments || !requests || !paymentsRef.current) return undefined
        paymentsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        setHighlightPayments(true)
        const timer = setTimeout(() => setHighlightPayments(false), 2200)
        return () => clearTimeout(timer)
    }, [wantsPayments, requests])

    const updateStatus = (orderId, approved) => {
        const formData = new FormData();
        formData.append("order_id", orderId);
        formData.append("status", approved);
        setIsSubmitting(true)
        setSubmitError('')
        OrderService.updatePaymentConfirmation(formData).then(res => {
            setIsSubmitting(false)
            if (res.status === 200) {
                setShowPopup(false)
                setToast(approved ? 'Payment approved' : 'Payment declined')
                setRefreshKey(k => k + 1)
            }
        }).catch(error => {
            setIsSubmitting(false)
            setSubmitError('Could not update this payment. Please try again.')
        });
    }

    const showRequestPopup = (req) => {
        setSelectedObj(req)
        setSubmitError('')
        setShowPopup(true)
    }

    const pendingCount = requests ? requests.length : 0

    return (
        <main className='mv-page'>
            <div className='mv-page-head'>
                <div>
                    <h1 className='mv-list-title'>Deliveries</h1>
                    <p className='mv-page-sub'>Verify payments and move orders through delivery.</p>
                </div>
            </div>

            {loadError &&
                <EmptyState icon={<HiOutlineEmojiSad />} title='We could not load orders'
                    text='Please check your connection and try again.' actionLabel='Try again' onAction={() => setRefreshKey(k => k + 1)} />}

            {!loadError &&
                <>
                    <section ref={paymentsRef} className={'mv-section mv-section-first mv-pay-section' + (highlightPayments ? ' is-highlighted' : '')} aria-labelledby='pay-requests'>
                        <div className='mv-section-head'>
                            <h2 id='pay-requests' className='mv-section-title mv-title-with-badge'>
                                Payments to verify {pendingCount > 0 && <span className='mv-count-badge'>{pendingCount}</span>}
                            </h2>
                        </div>

                        {isLoading && !requests && <Skeleton height={76} radius='14px' />}

                        {requests && requests.length === 0 &&
                            <div className='mv-card mv-quiet'>
                                <HiCheckCircle className='mv-quiet-icon' /> All caught up — no payments waiting for verification.
                            </div>}

                        {requests && requests.length > 0 &&
                            <div className='mv-pay-list'>
                                {requests.map(req =>
                                    <div key={req.orderId} className='mv-pay-request'>
                                        <span className='mv-pay-request-icon' aria-hidden='true'><HiOutlineCash /></span>
                                        <div className='mv-pay-request-info'>
                                            <span className='mv-pay-request-name'>{req.name || 'Customer'}</span>
                                            <span className='mv-pay-request-meta'>
                                                <span className='mv-cell-mono' title={req.orderId}>{req.orderId}</span>
                                                <span className='mv-cell-mono'>{payReference(req)}</span>
                                                {req.contact &&
                                                    <a href={`tel:${req.contact}`} className='mv-pay-request-phone'><HiOutlinePhone /> {req.contact}</a>}
                                            </span>
                                        </div>
                                        <span className='mv-pay-request-amount'>{formatRupee(req.amount)}</span>
                                        <button type='button' className='mv-btn mv-btn-primary mv-btn-sm' onClick={() => showRequestPopup(req)}>
                                            Review
                                        </button>
                                    </div>
                                )}
                            </div>}
                    </section>

                    <section className='mv-section' aria-labelledby='orders-title'>
                        <div className='mv-section-head'>
                            <h2 id='orders-title' className='mv-section-title'>Orders</h2>
                        </div>

                        <div className='mv-chip-row' role='tablist' aria-label='Filter orders by status'>
                            {FILTERS.map(f =>
                                <button type='button' key={f.value} role='tab' aria-selected={status === f.value}
                                    className={status === f.value ? 'mv-filter-chip active' : 'mv-filter-chip'}
                                    onClick={() => selectStatus(f.value)}>
                                    {f.label}
                                </button>
                            )}
                        </div>

                        {isLoading &&
                            <div className='mv-stack'>
                                {[0, 1, 2].map(i => <Skeleton key={i} height={52} radius='10px' />)}
                            </div>}

                        {!isLoading && orders && orders.length === 0 &&
                            <EmptyState icon={<HiOutlineInbox />} title='No orders here'
                                text={`There are no ${status === 0 ? '' : FILTERS.find(f => f.value === status).label.toLowerCase() + ' '}orders right now.`} />}

                        {!isLoading && orders && orders.length > 0 &&
                            <div className='mv-table-wrap'>
                                <table className='mv-table'>
                                    <thead>
                                        <tr>
                                            <th scope="col">Order</th>
                                            <th scope="col" className='mv-hide-mobile'>Customer</th>
                                            <th scope="col" className='mv-hide-mobile'>Payment ID</th>
                                            <th scope="col">Pincode</th>
                                            <th scope="col" className='mv-hide-mobile mv-cell-right'>Total</th>
                                            <th scope="col">Status</th>
                                            <th scope="col" className='mv-hide-mobile'><span className='mv-visually-hidden'>Open</span></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {orders.map(order => {
                                            const code = statusFromCode(order.status)
                                            return (
                                                <tr key={order.id} className='mv-row-link' onClick={() => redirctAdminEachOrder(order.id)}>
                                                    <td className='mv-cell-mono mv-cell-strong' title={order.orderId}>{order.orderId}</td>
                                                    <td className='mv-hide-mobile'>{order.userName}</td>
                                                    <td className='mv-hide-mobile mv-cell-mono' title={order.paymentId}>{order.paymentId || '–'}</td>
                                                    <td>{order.deliveryPincode}</td>
                                                    <td className='mv-hide-mobile mv-cell-right mv-cell-strong'>
                                                        {formatRupee(order.totalPrice)}
                                                        {order.couponCode && <span className='mv-coupon-code mv-coupon-code-sm mv-order-coupon-chip' title={`Coupon ${order.couponCode}`}>{order.couponCode}</span>}
                                                    </td>
                                                    <td>{code && <span className={`mv-status mv-status-${statusTone(code)}`}>{statusLabel(code)}</span>}</td>
                                                    <td className='mv-cell-right mv-hide-mobile'>
                                                        <button type='button' className='mv-icon-btn' aria-label={`Open order ${order.orderId}`}
                                                            onClick={(e) => { e.stopPropagation(); redirctAdminEachOrder(order.id) }}>
                                                            <MdNavigateNext />
                                                        </button>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>}
                    </section>
                </>
            }

            {showPopup && (
                <Modal
                    title='Verify payment'
                    onClose={() => setShowPopup(false)}
                    busy={isSubmitting}
                    actions={<>
                        <button type='button' className='mv-btn mv-btn-secondary' onClick={() => setShowPopup(false)} disabled={isSubmitting}>Cancel</button>
                        <button type='button' className='mv-btn mv-btn-danger' onClick={() => updateStatus(selectedObj.orderId, false)} disabled={isSubmitting}>Decline</button>
                        <button type='button' className='mv-btn mv-btn-success' onClick={() => updateStatus(selectedObj.orderId, true)} disabled={isSubmitting}>
                            {isSubmitting ? 'Saving…' : 'Approve'}
                        </button>
                    </>}
                >
                    <p className='mv-modal-text'>Has this payment reached your account? Check your bank or UPI app for the amount{selectedObj.utr ? ' and UTR' : ''}.</p>
                    <dl className='mv-detail-list'>
                        <dt>Customer</dt><dd>{selectedObj.name || '–'}</dd>
                        {selectedObj.contact && <><dt>Contact</dt><dd><a href={`tel:${selectedObj.contact}`}>{selectedObj.contact}</a></dd></>}
                        <dt>Order</dt><dd className='mv-cell-mono'>{selectedObj.orderId}</dd>
                        <dt>Paid with</dt><dd>{methodLabel(selectedObj)}</dd>
                        {selectedObj.utr &&
                            <><dt>UTR</dt><dd className='mv-cell-mono'>{selectedObj.utr}</dd></>}
                        <dt>Amount</dt><dd className='mv-detail-amount'>{formatRupee(selectedObj.amount)}</dd>
                    </dl>
                    {submitError && <div className='mv-alert mv-alert-error' role='alert'>{submitError}</div>}
                </Modal>
            )}

            <Toast message={toast} onDone={() => setToast('')} />
        </main>
    )
}

export default AdminOrderView
